import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Palette, Type, Layout, Zap, Check, X, DollarSign, Plus, Trash2, GripVertical, Edit2, ImageIcon, Upload, Share2, ExternalLink, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from '../utils/toast';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useBrandLogo } from '../contexts/BrandLogoContext';
import { useBrandFavicon } from '../contexts/BrandFaviconContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CMS_TABS = [
  { key: 'branding', label: 'Branding' },
  { key: 'landing', label: 'Landing Page' },
  { key: 'solution', label: 'Solution Page' },
  { key: 'pricing', label: 'Pricing Page' },
  { key: 'faqs', label: 'FAQs' },
  { key: 'theme', label: 'Theme Settings' },
  { key: 'poweredBy', label: 'Powered By' },
  { key: 'socialMedia', label: 'Social Media' },
  { key: 'integrations', label: 'Integrations' }
];

const CMSSettingsPage = () => {
  const navigate = useNavigate();
  const { setBrandLogo: setGlobalBrandLogo, DEFAULT_LOGO } = useBrandLogo();
  const { setBrandFavicon: setGlobalBrandFavicon, DEFAULT_FAVICON, updateFaviconLink } = useBrandFavicon();
  const [copiedToken, setCopiedToken] = useState('');
  const [user, setUser] = useState(null);
  const [privacyPolicy, setPrivacyPolicy] = useState('');
  const [termsConditions, setTermsConditions] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  
  // Pricing Plans State
  const [pricingPlans, setPricingPlans] = useState({});
  const [editingPlan, setEditingPlan] = useState(null);
  const [planFeaturesModalOpen, setPlanFeaturesModalOpen] = useState(false);
  const [editingFeatures, setEditingFeatures] = useState([]);
  const [newFeatureText, setNewFeatureText] = useState('');
  const [pricingLoading, setPricingLoading] = useState(false);

  // Tracker Background State
  const [trackerBgImage, setTrackerBgImage] = useState(null);
  const [trackerBgUploading, setTrackerBgUploading] = useState(false);

  // Brand Logo State
  const [brandLogo, setBrandLogo] = useState(null);
  const [brandLogoUploading, setBrandLogoUploading] = useState(false);
  const [brandLogoSaving, setBrandLogoSaving] = useState(false);

  // Brand Favicon State
  const [brandFavicon, setBrandFavicon] = useState(null);
  const [brandFaviconUploading, setBrandFaviconUploading] = useState(false);
  const [brandFaviconSaving, setBrandFaviconSaving] = useState(false);

  // Powered By Branding State
  const [poweredByLogo, setPoweredByLogo] = useState(null);
  const [poweredByAltName, setPoweredByAltName] = useState('BioQuest');
  const [poweredBySaving, setPoweredBySaving] = useState(false);
  const [poweredByUploading, setPoweredByUploading] = useState(false);

  // Theme Settings State
  const [themeSettings, setThemeSettings] = useState({
    sidebar_bg_color: '#FFFFFF',
    header_bg_color: '#FFFFFF',
    content_bg_color: '#FFFFFF',
    chat_bg_color: '#FFFFFF'
  });
  const [themeSaving, setThemeSaving] = useState(false);

  // FAQ Management State
  const [faqs, setFaqs] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [faqSaving, setFaqSaving] = useState(false);
  const [newFaqQuestion, setNewFaqQuestion] = useState('');
  const [newFaqAnswer, setNewFaqAnswer] = useState('');
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [editingFaqQuestion, setEditingFaqQuestion] = useState('');
  const [editingFaqAnswer, setEditingFaqAnswer] = useState('');

  // Social Media State
  const [socialChannels, setSocialChannels] = useState([]);
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialSaving, setSocialSaving] = useState(false);
  const [editingSocialId, setEditingSocialId] = useState(null);
  const [editingSocialData, setEditingSocialData] = useState({ name: '', url: '' });
  const [newSocialName, setNewSocialName] = useState('');
  const [newSocialUrl, setNewSocialUrl] = useState('');
  const [socialIconUploading, setSocialIconUploading] = useState(null);

  // Solution Page Management State
  const [solutionContent, setSolutionContent] = useState({
    main_heading: '',
    main_description: '',
    staff_skills_title: '',
    staff_skills_description: '',
    htms_title: '',
    htms_content: '',
    htms_right_image_url: '',
    htms_right_media_s3_key: '',
    htms_right_media_type: 'image',
    htms_right_media_url: '',
    feature_points: [],
    htms_supporting_description: '',
    courses_section_title: '',
    courses_section_description: ''
  });
  const [solutionCards, setSolutionCards] = useState([]);
  const [solutionLoading, setSolutionLoading] = useState(false);
  const [solutionSaving, setSolutionSaving] = useState(false);
  const [solutionMediaUploading, setSolutionMediaUploading] = useState(false);
  const [newSolutionCard, setNewSolutionCard] = useState({ title: '', description: '', icon_key: '', image_url: '' });
  const [editingSolutionCardId, setEditingSolutionCardId] = useState(null);
  const [editingSolutionCard, setEditingSolutionCard] = useState({ title: '', description: '', icon_key: '', image_url: '' });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchPolicies();
    fetchAppVersion();
    fetchPricingPlans();
    fetchTrackerBackground();
    fetchBrandLogo();
    fetchBrandFavicon();
    fetchPoweredByBranding();
    fetchThemeSettings();
    fetchFaqs();
    fetchSocialMedia();
    fetchSolutionPageContent();
  }, []);

  const isSuperAdmin = user?.role === 'Super Admin';
  const canManageBranding = ['Super Admin', 'ExlvaAdmin', 'Exlva Admin'].includes(user?.role);
  const canManageFaqs = ['Super Admin', 'ExlvaAdmin', 'Exlva Admin', 'Admin'].includes(user?.role);
  const canManageSolutionPage = ['Super Admin', 'ExlvaAdmin', 'Exlva Admin', 'Admin'].includes(user?.role);
  const hasPartialCmsAccess = !isSuperAdmin && (canManageFaqs || canManageSolutionPage);

  useEffect(() => {
    if (!isSuperAdmin && canManageSolutionPage) {
      setActiveTab('solution');
    } else if (!isSuperAdmin && canManageFaqs) {
      setActiveTab('faqs');
    }
  }, [isSuperAdmin, canManageFaqs, canManageSolutionPage]);

  const getAuthTokenOrRedirect = () => {
    const token = localStorage.getItem('token');
    if (!token || token === 'null' || token === 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      toast.error('Session expired. Please sign in again.');
      navigate('/signin');
      return null;
    }
    return token;
  };

  const isAuthenticationError = (error) => {
    const statusCode = error?.response?.status;
    const detail = (error?.response?.data?.detail || '').toString().toLowerCase();
    return (
      statusCode === 401 ||
      detail.includes('invalid authentication credential') ||
      detail.includes('invalid authentication credentials') ||
      detail.includes('token expired')
    );
  };

  const handleAuthenticationError = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.error('Session expired. Please sign in again.');
    navigate('/signin');
  };

  const fetchPolicies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cms/policies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrivacyPolicy(response.data.privacy_policy || '');
      setTermsConditions(response.data.terms_conditions || '');
    } catch (error) {
      console.error('Failed to fetch policies:', error);
    }
  };

  const fetchAppVersion = async () => {
    try {
      const response = await axios.get(`${API}/cms/version`);
      setAppVersion(response.data.version || '1.0.0');
    } catch (error) {
      console.error('Failed to fetch app version:', error);
      setAppVersion('1.0.0');
    }
  };

  const fetchPricingPlans = async () => {
    try {
      const response = await axios.get(`${API}/cms/pricing-plans`);
      setPricingPlans(response.data.plans || {});
    } catch (error) {
      console.error('Failed to fetch pricing plans:', error);
    }
  };

  const openPlanFeaturesModal = (planType) => {
    setEditingPlan(planType);
    const plan = pricingPlans[planType];
    setEditingFeatures(plan?.features || []);
    setPlanFeaturesModalOpen(true);
  };

  const addFeature = () => {
    if (!newFeatureText.trim()) return;
    setEditingFeatures([
      ...editingFeatures,
      { text: newFeatureText.trim(), is_included: true, is_bold: false }
    ]);
    setNewFeatureText('');
  };

  const removeFeature = (index) => {
    setEditingFeatures(editingFeatures.filter((_, i) => i !== index));
  };

  const toggleFeatureIncluded = (index) => {
    const updated = [...editingFeatures];
    updated[index].is_included = !updated[index].is_included;
    setEditingFeatures(updated);
  };

  const toggleFeatureBold = (index) => {
    const updated = [...editingFeatures];
    updated[index].is_bold = !updated[index].is_bold;
    setEditingFeatures(updated);
  };

  const updateFeatureText = (index, text) => {
    const updated = [...editingFeatures];
    updated[index].text = text;
    setEditingFeatures(updated);
  };

  const savePlanFeatures = async () => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can edit pricing plans');
      return;
    }

    setPricingLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cms/pricing-plans`, {
        plan_type: editingPlan,
        features: editingFeatures
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Pricing plan updated successfully!');
      fetchPricingPlans();
      setPlanFeaturesModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing plan');
    }
    setPricingLoading(false);
  };

  const handleSaveVersion = async () => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can edit application version');
      return;
    }

    if (!appVersion || !appVersion.match(/^\d+\.\d+\.\d+$/)) {
      toast.error('Invalid version format. Use X.Y.Z format (e.g., 1.2.3)');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cms/version`, { version: appVersion }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Application version updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update version');
    }
    setLoading(false);
  };

  // Tracker Background
  const fetchTrackerBackground = async () => {
    try {
      const response = await axios.get(`${API}/cms/tracker-background`);
      setTrackerBgImage(response.data.background_image || null);
    } catch (error) {
      console.error('Failed to fetch tracker background:', error);
    }
  };

  const handleTrackerBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can upload tracker background');
      return;
    }
    setTrackerBgUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/cms/tracker-background/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setTrackerBgImage(response.data.background_image);
      toast.success('Tracker background uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload background image');
    }
    setTrackerBgUploading(false);
  };

  const handleTrackerBgRemove = async () => {
    if (!isSuperAdmin) return;
    setTrackerBgUploading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cms/tracker-background`, { background_image: null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrackerBgImage(null);
      toast.success('Tracker background removed. Default gradient will be used.');
    } catch (error) {
      toast.error('Failed to remove background image');
    }
    setTrackerBgUploading(false);
  };

  // Brand Logo
  const fetchBrandLogo = async () => {
    try {
      const response = await axios.get(`${API}/cms/brand-logo`);
      setBrandLogo(response.data.brand_logo || null);
    } catch (error) {
      console.error('Failed to fetch Brand Logo:', error);
    }
  };

  const handleBrandLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can upload Brand Logo');
      return;
    }

    setBrandLogoUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/cms/brand-logo/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      let logoUrl = response.data.brand_logo;
      // Resolve relative proxy URL to full URL
      if (logoUrl && logoUrl.startsWith('/api/')) {
        logoUrl = `${BACKEND_URL}${logoUrl}`;
      }
      setBrandLogo(logoUrl);
      setGlobalBrandLogo(logoUrl);
      toast.success('Brand Logo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload Brand Logo');
    }
    setBrandLogoUploading(false);
  };

  const handleRemoveBrandLogo = async () => {
    if (!isSuperAdmin) return;
    setBrandLogoSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cms/brand-logo`, { brand_logo: null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrandLogo(null);
      setGlobalBrandLogo(DEFAULT_LOGO);
      toast.success('Brand Logo removed. Default Eptral.Hospital logo will be used.');
    } catch (error) {
      toast.error('Failed to remove Brand Logo');
    }
    setBrandLogoSaving(false);
  };

  // Brand Favicon
  const fetchBrandFavicon = async () => {
    try {
      const response = await axios.get(`${API}/cms/brand-favicon`);
      setBrandFavicon(response.data.brand_favicon || null);
    } catch (error) {
      console.error('Failed to fetch Brand Favicon:', error);
    }
  };

  const handleBrandFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!canManageBranding) {
      toast.error('Only Super Admin or Exlva Admin can upload Favicon');
      return;
    }

    setBrandFaviconUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/cms/brand-favicon/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      let faviconUrl = response.data.brand_favicon;
      if (faviconUrl && faviconUrl.startsWith('/api/')) {
        faviconUrl = `${BACKEND_URL}${faviconUrl}`;
      }
      setBrandFavicon(faviconUrl);
      setGlobalBrandFavicon(faviconUrl);
      updateFaviconLink(faviconUrl);
      toast.success('Brand Favicon uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload Brand Favicon');
    }
    setBrandFaviconUploading(false);
  };

  const handleRemoveBrandFavicon = async () => {
    if (!canManageBranding) return;
    setBrandFaviconSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cms/brand-favicon`, { brand_favicon: null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrandFavicon(null);
      setGlobalBrandFavicon(DEFAULT_FAVICON);
      updateFaviconLink(DEFAULT_FAVICON);
      toast.success('Brand Favicon removed. Default favicon will be used.');
    } catch (error) {
      toast.error('Failed to remove Brand Favicon');
    }
    setBrandFaviconSaving(false);
  };

  // Powered By Branding
  const fetchPoweredByBranding = async () => {
    try {
      const response = await axios.get(`${API}/cms/powered-by`);
      let logoUrl = response.data.powered_by_logo || null;
      if (logoUrl && logoUrl.startsWith('/api/')) {
        logoUrl = `${BACKEND_URL}${logoUrl}`;
      }
      setPoweredByLogo(logoUrl);
      setPoweredByAltName(response.data.powered_by_alt_name || 'BioQuest');
    } catch (error) {
      console.error('Failed to fetch Powered By branding:', error);
    }
  };

  const handlePoweredByLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can upload Powered By logo');
      return;
    }

    setPoweredByUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/cms/powered-by/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      let logoUrl = response.data.powered_by_logo;
      if (logoUrl && logoUrl.startsWith('/api/')) {
        logoUrl = `${BACKEND_URL}${logoUrl}`;
      }
      setPoweredByLogo(logoUrl);
      toast.success('Powered By logo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload Powered By logo');
    }
    setPoweredByUploading(false);
  };

  const handleSavePoweredByBranding = async () => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can update Powered By branding');
      return;
    }

    setPoweredBySaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/cms/powered-by`, {
        powered_by_logo: poweredByLogo,
        powered_by_alt_name: poweredByAltName || 'BioQuest'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPoweredByLogo(response.data.powered_by_logo || null);
      setPoweredByAltName(response.data.powered_by_alt_name || 'BioQuest');
      toast.success('Powered By branding saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Powered By branding');
    }
    setPoweredBySaving(false);
  };

  const handleRemovePoweredByLogo = async () => {
    if (!isSuperAdmin) return;
    setPoweredByLogo(null);
    setPoweredBySaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cms/powered-by`, {
        powered_by_logo: null,
        powered_by_alt_name: poweredByAltName || 'BioQuest'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Powered By logo removed. Alt name will be shown.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove Powered By logo');
      fetchPoweredByBranding();
    }
    setPoweredBySaving(false);
  };

  // Social Media
  const fetchSocialMedia = async () => {
    setSocialLoading(true);
    try {
      const response = await axios.get(`${API}/cms/social-media`);
      const channels = (response.data.channels || []).map(ch => {
        if (ch.icon_url && ch.icon_url.startsWith('/api/')) {
          ch.icon_url = `${BACKEND_URL}${ch.icon_url}`;
        }
        return ch;
      });
      setSocialChannels(channels);
    } catch (error) {
      console.error('Failed to fetch social media:', error);
    }
    setSocialLoading(false);
  };

  const handleAddSocialChannel = async () => {
    if (!newSocialName.trim() || !newSocialUrl.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    setSocialSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cms/social-media`, {
        name: newSocialName.trim(),
        url: newSocialUrl.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewSocialName('');
      setNewSocialUrl('');
      await fetchSocialMedia();
      toast.success('Social media channel added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add channel');
    }
    setSocialSaving(false);
  };

  const handleUpdateSocialChannel = async (channelId) => {
    setSocialSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cms/social-media/${channelId}`, editingSocialData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingSocialId(null);
      setEditingSocialData({ name: '', url: '' });
      await fetchSocialMedia();
      toast.success('Channel updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update channel');
    }
    setSocialSaving(false);
  };

  const handleToggleSocialChannel = async (channelId, currentActive) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cms/social-media/${channelId}`, {
        is_active: !currentActive
      }, { headers: { Authorization: `Bearer ${token}` } });
      await fetchSocialMedia();
      toast.success(`Channel ${!currentActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update channel status');
    }
  };

  const handleDeleteSocialChannel = async (channelId, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/cms/social-media/${channelId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchSocialMedia();
      toast.success('Channel deleted');
    } catch (error) {
      toast.error('Failed to delete channel');
    }
  };

  const handleSocialIconUpload = async (channelId, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setSocialIconUploading(channelId);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/cms/social-media/${channelId}/icon`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      await fetchSocialMedia();
      toast.success('Icon uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload icon');
    }
    setSocialIconUploading(null);
  };

  // Theme Settings
  const fetchThemeSettings = async () => {
    try {
      const response = await axios.get(`${API}/cms/theme-settings`);
      setThemeSettings({
        sidebar_bg_color: response.data.sidebar_bg_color || '#FFFFFF',
        header_bg_color: response.data.header_bg_color || '#FFFFFF',
        content_bg_color: response.data.content_bg_color || '#FFFFFF',
        chat_bg_color: response.data.chat_bg_color || '#FFFFFF'
      });
    } catch (error) {
      console.error('Failed to fetch theme settings:', error);
    }
  };

  const handleSaveThemeSettings = async () => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can update theme settings');
      return;
    }
    setThemeSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cms/theme-settings`, themeSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Theme settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save theme settings');
    }
    setThemeSaving(false);
  };

  const handleResetTheme = () => {
    setThemeSettings({
      sidebar_bg_color: '#FFFFFF',
      header_bg_color: '#FFFFFF',
      content_bg_color: '#FFFFFF',
      chat_bg_color: '#FFFFFF'
    });
  };

  const fetchFaqs = async () => {
    setFaqLoading(true);
    try {
      const response = await axios.get(`${API}/cms/faqs`);
      setFaqs(Array.isArray(response.data.faqs) ? response.data.faqs : []);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
      toast.error('Failed to load FAQs');
    }
    setFaqLoading(false);
  };

  const handleAddFaq = async () => {
    if (!canManageFaqs) {
      toast.error('Only Super Admin and Exlva Admin can manage FAQs');
      return;
    }

    const question = newFaqQuestion.trim();
    const answer = newFaqAnswer.trim();
    if (!question || !answer) {
      toast.error('Question and answer are required');
      return;
    }

    setFaqSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/cms/faqs`,
        { question, answer },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewFaqQuestion('');
      setNewFaqAnswer('');
      await fetchFaqs();
      toast.success('FAQ added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add FAQ');
    }
    setFaqSaving(false);
  };

  const startEditFaq = (faq) => {
    setEditingFaqId(faq.faq_id);
    setEditingFaqQuestion(faq.question || '');
    setEditingFaqAnswer(faq.answer || '');
  };

  const cancelEditFaq = () => {
    setEditingFaqId(null);
    setEditingFaqQuestion('');
    setEditingFaqAnswer('');
  };

  const handleUpdateFaq = async (faqId) => {
    if (!canManageFaqs) {
      toast.error('Only Super Admin and Exlva Admin can manage FAQs');
      return;
    }

    const question = editingFaqQuestion.trim();
    const answer = editingFaqAnswer.trim();
    if (!question || !answer) {
      toast.error('Question and answer are required');
      return;
    }

    setFaqSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/cms/faqs/${faqId}`,
        { question, answer },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      cancelEditFaq();
      await fetchFaqs();
      toast.success('FAQ updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update FAQ');
    }
    setFaqSaving(false);
  };

  const handleDeleteFaq = async (faqId) => {
    if (!canManageFaqs) {
      toast.error('Only Super Admin and Exlva Admin can manage FAQs');
      return;
    }

    setFaqSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/cms/faqs/${faqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (editingFaqId === faqId) {
        cancelEditFaq();
      }
      await fetchFaqs();
      toast.success('FAQ deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete FAQ');
    }
    setFaqSaving(false);
  };

  const handleReorderFaq = async (faqId, direction) => {
    if (!canManageFaqs) {
      toast.error('Only Super Admin and Exlva Admin can manage FAQs');
      return;
    }

    const currentIndex = faqs.findIndex((item) => item.faq_id === faqId);
    if (currentIndex === -1) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= faqs.length) {
      return;
    }

    const reorderedFaqs = [...faqs];
    [reorderedFaqs[currentIndex], reorderedFaqs[targetIndex]] = [reorderedFaqs[targetIndex], reorderedFaqs[currentIndex]];
    setFaqs(reorderedFaqs);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/cms/faqs/reorder`,
        { faq_ids: reorderedFaqs.map((item) => item.faq_id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reorder FAQ');
      await fetchFaqs();
    }
  };

  const fetchSolutionPageContent = async () => {
    setSolutionLoading(true);
    try {
      const response = await axios.get(`${API}/cms/solution-page`);
      const content = response.data?.content || {};
      setSolutionContent({
        main_heading: content.main_heading || '',
        main_description: content.main_description || '',
        staff_skills_title: content.staff_skills_title || '',
        staff_skills_description: content.staff_skills_description || '',
        htms_title: content.htms_title || '',
        htms_content: content.htms_content || '',
        htms_right_image_url: content.htms_right_image_url || '',
        htms_right_media_s3_key: content.htms_right_media_s3_key || '',
        htms_right_media_type: content.htms_right_media_type || 'image',
        htms_right_media_url: content.htms_right_media_url || content.htms_right_image_url || '',
        feature_points: Array.isArray(content.feature_points) ? content.feature_points : [],
        htms_supporting_description: content.htms_supporting_description || '',
        courses_section_title: content.courses_section_title || '',
        courses_section_description: content.courses_section_description || ''
      });
      setSolutionCards(Array.isArray(content.cards) ? content.cards : []);
    } catch (error) {
      console.error('Failed to fetch solution page content:', error);
      toast.error('Failed to load Solution page content');
    }
    setSolutionLoading(false);
  };

  const handleSaveSolutionContent = async () => {
    if (!canManageSolutionPage) {
      toast.error('Only Super Admin and Exlva Admin can manage Solution page content');
      return;
    }

    setSolutionSaving(true);
    try {
      const token = getAuthTokenOrRedirect();
      if (!token) {
        setSolutionSaving(false);
        return;
      }
      const payload = {
        main_heading: solutionContent.main_heading,
        main_description: solutionContent.main_description,
        staff_skills_title: solutionContent.staff_skills_title,
        staff_skills_description: solutionContent.staff_skills_description,
        htms_title: solutionContent.htms_title,
        htms_content: solutionContent.htms_content,
        htms_right_image_url: solutionContent.htms_right_image_url,
        htms_right_media_s3_key: solutionContent.htms_right_media_s3_key,
        htms_right_media_type: solutionContent.htms_right_media_type,
        htms_supporting_description: solutionContent.htms_supporting_description,
        courses_section_title: solutionContent.courses_section_title,
        courses_section_description: solutionContent.courses_section_description,
        feature_points: (solutionContent.feature_points || []).filter((item) => (item || '').trim() !== ''),
      };

      await axios.put(`${API}/cms/solution-page`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Solution page content updated successfully');
      await fetchSolutionPageContent();
    } catch (error) {
      if (isAuthenticationError(error)) {
        handleAuthenticationError();
        setSolutionSaving(false);
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to update Solution page content');
    }
    setSolutionSaving(false);
  };

  const handleUploadSolutionMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!canManageSolutionPage) {
      toast.error('Only Super Admin and Exlva Admin can manage Solution page content');
      return;
    }

    setSolutionMediaUploading(true);
    try {
      const token = getAuthTokenOrRedirect();
      if (!token) {
        setSolutionMediaUploading(false);
        return;
      }
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/cms/solution-page/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const mediaType = response.data?.media_type || 'image';
      setSolutionContent((prev) => ({
        ...prev,
        htms_right_media_type: mediaType,
        htms_right_media_s3_key: response.data?.s3_key || '',
        htms_right_media_url: response.data?.signed_url || '',
      }));

      toast.success('HTMS media uploaded successfully');
    } catch (error) {
      if (isAuthenticationError(error)) {
        handleAuthenticationError();
        setSolutionMediaUploading(false);
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to upload HTMS media');
    }
    setSolutionMediaUploading(false);
    e.target.value = '';
  };

  const handleAddPointer = () => {
    setSolutionContent((prev) => ({
      ...prev,
      feature_points: [...(Array.isArray(prev.feature_points) ? prev.feature_points : []), '']
    }));
  };

  const handleUpdatePointer = (index, value) => {
    setSolutionContent((prev) => {
      const points = [...(Array.isArray(prev.feature_points) ? prev.feature_points : [])];
      points[index] = value;
      return { ...prev, feature_points: points };
    });
  };

  const handleDeletePointer = (index) => {
    setSolutionContent((prev) => ({
      ...prev,
      feature_points: (Array.isArray(prev.feature_points) ? prev.feature_points : []).filter((_, i) => i !== index)
    }));
  };

  const handleReorderPointer = (index, direction) => {
    setSolutionContent((prev) => {
      const points = [...(Array.isArray(prev.feature_points) ? prev.feature_points : [])];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= points.length) {
        return prev;
      }
      [points[index], points[targetIndex]] = [points[targetIndex], points[index]];
      return { ...prev, feature_points: points };
    });
  };

  const handleAddSolutionCard = async () => {
    if (!canManageSolutionPage) {
      toast.error('Only Super Admin and Exlva Admin can manage Solution page content');
      return;
    }

    if (!newSolutionCard.title.trim() || !newSolutionCard.description.trim()) {
      toast.error('Card title and description are required');
      return;
    }

    setSolutionSaving(true);
    try {
      const token = getAuthTokenOrRedirect();
      if (!token) {
        setSolutionSaving(false);
        return;
      }
      await axios.post(`${API}/cms/solution-page/cards`, {
        ...newSolutionCard,
        title: newSolutionCard.title.trim(),
        description: newSolutionCard.description.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewSolutionCard({ title: '', description: '', icon_key: '', image_url: '' });
      await fetchSolutionPageContent();
      toast.success('Solution card added successfully');
    } catch (error) {
      if (isAuthenticationError(error)) {
        handleAuthenticationError();
        setSolutionSaving(false);
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to add Solution card');
    }
    setSolutionSaving(false);
  };

  const startEditSolutionCard = (card) => {
    setEditingSolutionCardId(card.card_id);
    setEditingSolutionCard({
      title: card.title || '',
      description: card.description || '',
      icon_key: card.icon_key || '',
      image_url: card.image_url || ''
    });
  };

  const cancelEditSolutionCard = () => {
    setEditingSolutionCardId(null);
    setEditingSolutionCard({ title: '', description: '', icon_key: '', image_url: '' });
  };

  const handleUpdateSolutionCard = async (cardId) => {
    if (!canManageSolutionPage) {
      toast.error('Only Super Admin and Exlva Admin can manage Solution page content');
      return;
    }

    if (!editingSolutionCard.title.trim() || !editingSolutionCard.description.trim()) {
      toast.error('Card title and description are required');
      return;
    }

    setSolutionSaving(true);
    try {
      const token = getAuthTokenOrRedirect();
      if (!token) {
        setSolutionSaving(false);
        return;
      }
      await axios.put(`${API}/cms/solution-page/cards/${cardId}`, {
        ...editingSolutionCard,
        title: editingSolutionCard.title.trim(),
        description: editingSolutionCard.description.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      cancelEditSolutionCard();
      await fetchSolutionPageContent();
      toast.success('Solution card updated successfully');
    } catch (error) {
      if (isAuthenticationError(error)) {
        handleAuthenticationError();
        setSolutionSaving(false);
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to update Solution card');
    }
    setSolutionSaving(false);
  };

  const handleDeleteSolutionCard = async (cardId) => {
    if (!canManageSolutionPage) {
      toast.error('Only Super Admin and Exlva Admin can manage Solution page content');
      return;
    }

    setSolutionSaving(true);
    try {
      const token = getAuthTokenOrRedirect();
      if (!token) {
        setSolutionSaving(false);
        return;
      }
      await axios.delete(`${API}/cms/solution-page/cards/${cardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (editingSolutionCardId === cardId) {
        cancelEditSolutionCard();
      }
      await fetchSolutionPageContent();
      toast.success('Solution card deleted successfully');
    } catch (error) {
      if (isAuthenticationError(error)) {
        handleAuthenticationError();
        setSolutionSaving(false);
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to delete Solution card');
    }
    setSolutionSaving(false);
  };

  const handleReorderSolutionCard = async (cardId, direction) => {
    if (!canManageSolutionPage) {
      toast.error('Only Super Admin and Exlva Admin can manage Solution page content');
      return;
    }

    const currentIndex = solutionCards.findIndex((card) => card.card_id === cardId);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= solutionCards.length) return;

    const reordered = [...solutionCards];
    [reordered[currentIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[currentIndex]];
    setSolutionCards(reordered);

    try {
      const token = getAuthTokenOrRedirect();
      if (!token) {
        await fetchSolutionPageContent();
        return;
      }
      await axios.put(`${API}/cms/solution-page/cards/reorder`, {
        card_ids: reordered.map((card) => card.card_id)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      if (isAuthenticationError(error)) {
        handleAuthenticationError();
        await fetchSolutionPageContent();
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to reorder Solution cards');
      await fetchSolutionPageContent();
    }
  };

  const handleSavePolicy = async (type) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can edit CMS settings');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = type === 'privacy' 
        ? { privacy_policy: privacyPolicy }
        : { terms_conditions: termsConditions };

      await axios.put(`${API}/cms/policies`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${type === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions'} updated successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(text);
    setTimeout(() => setCopiedToken(''), 2000);
  };

  const ColorToken = ({ name, value, description }) => (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
      <div className="flex items-center space-x-3">
        <div 
          className="w-12 h-12 rounded-lg border border-gray-300" 
          style={{ backgroundColor: value }}
        ></div>
        <div>
          <p className="font-medium text-gray-900">{name}</p>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <code className="px-2 py-1 bg-gray-100 rounded text-sm">{value}</code>
        <button
          onClick={() => copyToClipboard(value)}
          className="p-2 hover:bg-gray-100 rounded"
        >
          {copiedToken === value ? <Check className="w-4 h-4 text-green-600" /> : <Type className="w-4 h-4 text-gray-600" />}
        </button>
      </div>
    </div>
  );

  const canViewTab = (tabKey) => {
    if (isSuperAdmin) return true;
    if (tabKey === 'solution') return canManageSolutionPage;
    if (tabKey === 'faqs') return canManageFaqs;
    return false;
  };

  const visibleTabs = CMS_TABS.filter((tab) => canViewTab(tab.key));

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Page Title */}
        <div className="flex items-center space-x-3 mb-6">
          <Palette className="w-8 h-8 text-[#0056B3]" />
          <h1 className="text-2xl font-bold text-gray-900">CMS SETTINGS</h1>
        </div>

        {/* Read-Only Notice for non-editable users */}
        {!isSuperAdmin && !hasPartialCmsAccess && (
          <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
            <div className="flex items-center">
              <X className="w-5 h-5 text-yellow-600 mr-3" />
              <div>
                <p className="font-semibold text-yellow-800">Read-Only Access</p>
                <p className="text-sm text-yellow-700">You are viewing CMS settings in read-only mode.</p>
              </div>
            </div>
          </div>
        )}

        {/* Partial Access Notice for Exlva Admin */}
        {!isSuperAdmin && hasPartialCmsAccess && (
          <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
            <div className="flex items-center">
              <Check className="w-5 h-5 text-blue-600 mr-3" />
              <div>
                <p className="font-semibold text-blue-800">Limited CMS Content Access</p>
                <p className="text-sm text-blue-700">You can edit FAQ Management and The Solution Page content here. Other CMS sections remain Super Admin only.</p>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6 border-b border-gray-200">
          <div className="flex flex-wrap gap-2 pb-3">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-[#0056B3] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Brand Identity */}
        <div className="space-y-8">
        {activeTab === 'branding' && (
        <>
        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Palette className="w-6 h-6 text-[#0056B3]" />
            <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>A. Brand Identity</h2>
          </div>

          <div className="space-y-6">
            {/* Brand Logo */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Brand Logo</h3>
              <div className="p-6 bg-white border rounded-lg">
                <div className="h-20 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-center px-4 overflow-hidden">
                  {brandLogo ? (
                    <img 
                      src={brandLogo} 
                      alt="Brand Logo" 
                      className="max-h-16 max-w-full object-contain"
                    />
                  ) : (
                    <img 
                      src="/brand-logo.svg" 
                      alt="Default Eptral.Hospital Logo" 
                      className="max-h-16 max-w-full object-contain opacity-50"
                    />
                  )}
                </div>

                {isSuperAdmin && (
                  <div className="flex flex-wrap items-center gap-3 mt-4">
                    <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-[#0056B3] text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                      <Upload className="w-4 h-4 mr-2" />
                      {brandLogoUploading ? 'Uploading...' : 'Upload Logo'}
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,.svg"
                        className="hidden"
                        onChange={handleBrandLogoUpload}
                        disabled={brandLogoUploading}
                      />
                    </label>
                    {brandLogo && (
                      <button
                        onClick={handleRemoveBrandLogo}
                        className="inline-flex items-center px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
                        disabled={brandLogoSaving || brandLogoUploading}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove Logo
                      </button>
                    )}
                  </div>
                )}

                <p className="text-sm text-gray-600 mt-4">
                  This logo appears globally across the application wherever default Eptral.Hospital branding is used. Hospital-specific logos take priority where configured.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: JPG, PNG, WebP, SVG. Recommended: transparent PNG, at least 200px wide.
                </p>
              </div>
            </div>

            {/* Brand Favicon */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Brand Favicon</h3>
              <div className="p-6 bg-white border rounded-lg">
                <div className="h-20 w-20 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden">
                  {brandFavicon ? (
                    <img
                      src={brandFavicon.startsWith('/api/') ? `${BACKEND_URL}${brandFavicon}` : brandFavicon}
                      alt="Brand Favicon"
                      className="max-h-16 max-w-16 object-contain"
                    />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  )}
                </div>

                {canManageBranding && (
                  <div className="flex flex-wrap items-center gap-3 mt-4">
                    <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-[#0056B3] text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                      <Upload className="w-4 h-4 mr-2" />
                      {brandFaviconUploading ? 'Uploading...' : 'Upload Favicon'}
                      <input
                        type="file"
                        accept=".ico,.png,.svg"
                        className="hidden"
                        onChange={handleBrandFaviconUpload}
                        disabled={brandFaviconUploading}
                      />
                    </label>
                    {brandFavicon && (
                      <button
                        onClick={handleRemoveBrandFavicon}
                        className="inline-flex items-center px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
                        disabled={brandFaviconSaving || brandFaviconUploading}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove Favicon
                      </button>
                    )}
                  </div>
                )}

                <p className="text-sm text-gray-600 mt-4">
                  This favicon appears in browser tabs, bookmarks, PWA home screen icons, and other places where the site icon is displayed.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: ICO, PNG, SVG. Recommended: 32x32 or 64x64 PNG with transparent background.
                </p>
              </div>
            </div>

            {/* Primary Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Primary Color Palette</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ColorToken name="Primary Blue" value="#0056B3" description="Brand blue - main actions" />
                <ColorToken name="Primary Dark" value="#004494" description="Hover states" />
                <ColorToken name="Secondary" value="#F5F6FA" description="Backgrounds" />
                <ColorToken name="Accent" value="#00AEEF" description="Interactive highlights" />
              </div>
            </div>

            {/* Alert Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Alert Colors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ColorToken name="Success" value="#2ECC71" description="Success states" />
                <ColorToken name="Error" value="#E63946" description="Error states" />
                <ColorToken name="Warning" value="#F39C12" description="Warning states" />
                <ColorToken name="Info" value="#3498DB" description="Info states" />
              </div>
            </div>

            {/* Typography */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Typography</h3>
              <div className="space-y-3 p-4 bg-white border rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Font Family</p>
                  <p className="text-[18px] font-semibold">Poppins, sans-serif</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div>
                    <p className="text-4xl font-bold mb-1">Aa</p>
                    <p className="text-sm text-gray-600">Headings</p>
                    <p className="text-xs text-gray-500">Semi-bold (600)</p>
                  </div>
                  <div>
                    <p className="text-[28px] font-medium mb-1">Aa</p>
                    <p className="text-sm text-gray-600">Subheadings</p>
                    <p className="text-xs text-gray-500">Medium (500)</p>
                  </div>
                  <div>
                    <p className="text-[22px] font-normal mb-1">Aa</p>
                    <p className="text-sm text-gray-600">Body Text</p>
                    <p className="text-xs text-gray-500">Regular (400)</p>
                  </div>
                  <div>
                    <p className="text-xs font-normal mb-1">AA</p>
                    <p className="text-sm text-gray-600">Captions</p>
                    <p className="text-xs text-gray-500">Small, Uppercase</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Form Elements */}
        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Layout className="w-6 h-6 text-[#0056B3]" />
            <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>C. Form Elements</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Text Input</label>
              <input 
                type="text" 
                placeholder="Enter text here..." 
                className="exlva-input"
              />
              <p className="text-xs text-gray-500 mt-1">Rounded corners, 1px gray border, blue focus state</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Input with Error</label>
              <input 
                type="text" 
                placeholder="Invalid input" 
                className="exlva-input error"
              />
              <p className="exlva-error-text">This field is required</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Disabled Input</label>
              <input 
                type="text" 
                placeholder="Disabled field" 
                className="exlva-input"
                disabled
              />
            </div>
          </div>
        </Card>

        {/* Buttons */}
        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Zap className="w-6 h-6 text-[#0056B3]" />
            <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>D. Interactive Elements - Buttons</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-3">Button Variants</h3>
              <div className="flex flex-wrap gap-3">
                <button className="exlva-btn exlva-btn-primary">Primary Button</button>
                <button className="exlva-btn exlva-btn-secondary">Secondary Button</button>
                <button className="exlva-btn exlva-btn-accent">Accent Button</button>
                <button className="exlva-btn exlva-btn-success">Success Button</button>
                <button className="exlva-btn exlva-btn-error">Error Button</button>
                <button className="exlva-btn exlva-btn-ghost">Ghost Button</button>
                <button className="exlva-btn exlva-btn-primary" disabled>Disabled Button</button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Button Sizes</h3>
              <div className="flex flex-wrap items-center gap-3">
                <button className="exlva-btn exlva-btn-primary exlva-btn-sm">Small Button</button>
                <button className="exlva-btn exlva-btn-primary">Default Button</button>
                <button className="exlva-btn exlva-btn-primary exlva-btn-lg">Large Button</button>
              </div>
            </div>
          </div>
        </Card>

        {/* Alerts */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>E. Feedback & Alerts</h2>
          
          <div className="space-y-4">
            <div className="exlva-alert exlva-alert-success">
              <strong>Success!</strong> Your changes have been saved successfully.
            </div>
            <div className="exlva-alert exlva-alert-error">
              <strong>Error!</strong> Something went wrong. Please try again.
            </div>
            <div className="exlva-alert exlva-alert-warning">
              <strong>Warning!</strong> Your subscription will expire in 3 days.
            </div>
            <div className="exlva-alert exlva-alert-info">
              <strong>Info!</strong> You have 5 new notifications to review.
            </div>
          </div>
        </Card>

        {/* Cards */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>F. Visual Elements - Cards</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="exlva-card">
              <div className="exlva-card-header">
                <h3 className="exlva-card-title">Card Title</h3>
              </div>
              <p className="text-gray-600">
                This is a card with rounded corners and shadow. Hover to see the elevation effect.
              </p>
            </div>
            <div className="exlva-card">
              <h3 className="text-lg font-semibold mb-2">Statistics Card</h3>
              <p className="text-[28px] font-bold" style={{ color: '#0056B3' }}>1,234</p>
              <p className="text-sm text-gray-600">Total Users</p>
            </div>
            <div className="exlva-card">
              <h3 className="text-lg font-semibold mb-2">Action Card</h3>
              <p className="text-gray-600 mb-4">Quick actions available here.</p>
              <button className="exlva-btn exlva-btn-primary exlva-btn-sm">Take Action</button>
            </div>
          </div>
        </Card>

        {/* Responsive Grid */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>B. Layout & Grid System</h2>
          
          <div className="space-y-4">
            <div className="exlva-grid exlva-grid-cols-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="p-4 bg-blue-100 rounded text-center">Col {i}</div>
              ))}
            </div>
            <p className="text-sm text-gray-600">
              12-column responsive grid system. Collapses to single column on mobile.
            </p>
          </div>
        </Card>

        {/* Animations */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>I. Animations & Transitions</h2>
          
          <div className="space-y-4">
            <div className="exlva-fade-in p-4 bg-blue-50 rounded">
              Fade In Animation
            </div>
            <div className="exlva-slide-in-up p-4 bg-green-50 rounded">
              Slide In Up Animation
            </div>
            <div className="flex items-center space-x-2">
              <div className="exlva-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
              <span>Loading Spinner</span>
            </div>
            <p className="text-sm text-gray-600">
              Transitions: 150-300ms with ease-in-out easing for smooth interactions.
            </p>
          </div>
        </Card>

        {/* Footer */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>J. Footer & Legal</h2>
          
          <div className="exlva-footer">
            <div className="mb-4">
              <img 
                src={brandLogo || "/brand-logo.svg"} 
                alt="Brand Logo" 
                className="h-8 w-auto object-contain mx-auto"
              />
            </div>
            <div className="exlva-footer-links">
              <a href="#" className="exlva-footer-link">Terms & Conditions</a>
              <a href="#" className="exlva-footer-link">Privacy Policy</a>
              <a href="#" className="exlva-footer-link">Contact Us</a>
            </div>
            <div className="exlva-footer-content">
              © 2025 Eptral.Hospital. All rights reserved.
            </div>
          </div>
        </Card>

        {/* Accessibility */}
        <Card className="p-6">
          <h2 className="text-[22px] font-semibold mb-6" style={{ color: '#0056B3' }}>G. Accessibility Standards</h2>
          
          <div className="space-y-3">
            <div className="flex items-start space-x-2">
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
              <p>Minimum contrast ratio of 4.5:1 for text and background</p>
            </div>
            <div className="flex items-start space-x-2">
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
              <p>Full keyboard navigation support with visible focus states</p>
            </div>
            <div className="flex items-start space-x-2">
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
              <p>ARIA labels and semantic HTML elements</p>
            </div>
            <div className="flex items-start space-x-2">
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
              <p>Responsive text scaling across all screen sizes</p>
            </div>
          </div>
        </Card>

        {/* Usage Guide */}
        <Card className="p-6 bg-blue-50 border-blue-200">
          <h2 className="text-[22px] font-semibold mb-4" style={{ color: '#0056B3' }}>Implementation Guide</h2>
          <div className="space-y-3 text-sm">
            <p>
              <strong>CSS Classes:</strong> Use <code className="bg-white px-2 py-1 rounded">exlva-*</code> prefixed classes for design system components.
            </p>
            <p>
              <strong>CSS Variables:</strong> Use <code className="bg-white px-2 py-1 rounded">var(--exlva-primary)</code> for colors in custom CSS.
            </p>
            <p>
              <strong>Import:</strong> The design system is automatically imported via <code className="bg-white px-2 py-1 rounded">index.css</code>.
            </p>
            <p>
              <strong>File:</strong> <code className="bg-white px-2 py-1 rounded">/app/frontend/src/exlva-design-system.css</code>
            </p>
          </div>
        </Card>
        </>
        )}

        {/* Application Version Subchapter */}
        {activeTab === 'landing' && (
        <>
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Zap className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Application Version</h2>
            </div>
            {isSuperAdmin && (
              <Button 
                onClick={handleSaveVersion}
                className="bg-[#0056B3] hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Version'}
              </Button>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Application Version
            </label>
            <input
              type="text"
              className="w-full max-w-xs p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="1.0.0"
              disabled={!isSuperAdmin}
              value={appVersion}
              onChange={(e) => setAppVersion(e.target.value)}
            />
            <p className="text-sm text-gray-600 mt-2">
              Version format: <code className="bg-gray-100 px-2 py-1 rounded">X.Y.Z</code> (e.g., 1.2.3). This version will be displayed in the footer of all pages.
            </p>
          </div>
        </Card>

        {/* Tracker Background Subchapter */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <ImageIcon className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Learning Journey Tracker Background</h2>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Customize the background of the Learning Journey Tracker displayed in Adaptive Learning mode. Upload an image to use as a tiled background, or leave empty to use the default sky-blue / grass-green gradient.
          </p>

          {/* Current Background Preview */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Background</label>
            <div
              className="w-full h-24 rounded-lg border border-gray-200 overflow-hidden"
              style={trackerBgImage
                ? { backgroundImage: `url(${trackerBgImage})`, backgroundRepeat: 'repeat', backgroundSize: 'auto' }
                : { background: 'linear-gradient(to bottom, #87CEEB 0%, #87CEEB 80%, #4CAF50 80%, #4CAF50 100%)' }
              }
            >
              {!trackerBgImage && (
                <div className="flex items-center justify-center h-full text-white text-sm font-medium" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                  Default Gradient (Sky Blue + Grass Green)
                </div>
              )}
            </div>
          </div>

          {isSuperAdmin && (
            <div className="flex items-center space-x-3">
              <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-[#0056B3] text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                <Upload className="w-4 h-4 mr-2" />
                {trackerBgUploading ? 'Uploading...' : 'Upload Background Image'}
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.svg"
                  className="hidden"
                  onChange={handleTrackerBgUpload}
                  disabled={trackerBgUploading}
                />
              </label>
              {trackerBgImage && (
                <button
                  onClick={handleTrackerBgRemove}
                  className="inline-flex items-center px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
                  disabled={trackerBgUploading}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove & Use Default Gradient
                </button>
              )}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-3">
            Supported formats: JPG, PNG, WebP, SVG. The image will be tiled (repeated) across the tracker container.
          </p>
        </Card>
        </>
        )}

        {/* Powered By Branding */}
        {activeTab === 'poweredBy' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <ImageIcon className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Powered By Branding</h2>
            </div>
            {isSuperAdmin && (
              <Button
                onClick={handleSavePoweredByBranding}
                className="bg-[#0056B3] hover:bg-blue-700"
                disabled={poweredBySaving || poweredByUploading}
              >
                {poweredBySaving ? 'Saving...' : 'Save Branding'}
              </Button>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Manage the compact Powered By branding shown in the bottom-left application area. If no logo is uploaded, the alt name is displayed.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Powered By Logo</label>
              <div className="h-20 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-center px-4 overflow-hidden">
                {poweredByLogo ? (
                  <img
                    src={poweredByLogo}
                    alt={poweredByAltName || 'Powered By logo'}
                    className="max-h-10 max-w-full object-contain"
                  />
                ) : (
                  <span className="text-sm text-gray-500">No logo uploaded</span>
                )}
              </div>

              {isSuperAdmin && (
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-[#0056B3] text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                    <Upload className="w-4 h-4 mr-2" />
                    {poweredByUploading ? 'Uploading...' : 'Upload Logo'}
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.svg"
                      className="hidden"
                      onChange={handlePoweredByLogoUpload}
                      disabled={poweredByUploading}
                    />
                  </label>
                  {poweredByLogo && (
                    <button
                      onClick={handleRemovePoweredByLogo}
                      className="inline-flex items-center px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
                      disabled={poweredBySaving || poweredByUploading}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove Logo
                    </button>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Powered By Alt Name</label>
              <Input
                value={poweredByAltName}
                onChange={(e) => setPoweredByAltName(e.target.value)}
                placeholder="BioQuest"
                disabled={!isSuperAdmin}
              />
              <p className="text-xs text-gray-500 mt-2">
                Used as fallback text and as the uploaded logo alt text.
              </p>

              <div className="mt-4 rounded-lg border border-gray-200 p-3 bg-white">
                <p className="text-xs font-medium text-gray-500 mb-2">Compact Preview</p>
                <div className="text-[11px] text-gray-500 inline-flex items-center gap-1 max-w-[150px] overflow-hidden">
                  <span>Powered by</span>
                  {poweredByLogo ? (
                    <img src={poweredByLogo} alt={poweredByAltName || 'Powered By logo'} className="max-h-4 max-w-[74px] object-contain" />
                  ) : (
                    <span className="truncate font-medium">{poweredByAltName || 'BioQuest'}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            Supported formats: JPG, PNG, WebP, SVG. The displayed logo is constrained to the existing footer/sidebar space and scales proportionally.
          </p>
        </Card>
        )}

        {/* Theme Customization Subchapter */}
        {activeTab === 'theme' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Palette className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Theme Customization</h2>
            </div>
            {isSuperAdmin && (
              <div className="flex items-center space-x-2">
                <Button
                  onClick={handleResetTheme}
                  variant="outline"
                  className="text-sm"
                >
                  Reset to Default
                </Button>
                <Button 
                  onClick={handleSaveThemeSettings}
                  className="bg-[#0056B3] hover:bg-blue-700"
                  disabled={themeSaving}
                >
                  {themeSaving ? 'Saving...' : 'Save Theme'}
                </Button>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Customize the sidebar, header, and content area background colors. Changes apply to all user roles.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Sidebar Background */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sidebar Background</label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={themeSettings.sidebar_bg_color}
                  onChange={(e) => setThemeSettings(prev => ({ ...prev, sidebar_bg_color: e.target.value }))}
                  className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  disabled={!isSuperAdmin}
                />
                <input
                  type="text"
                  value={themeSettings.sidebar_bg_color}
                  onChange={(e) => setThemeSettings(prev => ({ ...prev, sidebar_bg_color: e.target.value }))}
                  className="flex-1 p-2 border border-gray-300 rounded-lg text-sm uppercase"
                  placeholder="#FFFFFF"
                  disabled={!isSuperAdmin}
                />
              </div>
              <div className="mt-2 h-8 rounded border border-gray-200" style={{ backgroundColor: themeSettings.sidebar_bg_color }} />
            </div>

            {/* Header Background */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Header Background</label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={themeSettings.header_bg_color}
                  onChange={(e) => setThemeSettings(prev => ({ ...prev, header_bg_color: e.target.value }))}
                  className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  disabled={!isSuperAdmin}
                />
                <input
                  type="text"
                  value={themeSettings.header_bg_color}
                  onChange={(e) => setThemeSettings(prev => ({ ...prev, header_bg_color: e.target.value }))}
                  className="flex-1 p-2 border border-gray-300 rounded-lg text-sm uppercase"
                  placeholder="#FFFFFF"
                  disabled={!isSuperAdmin}
                />
              </div>
              <div className="mt-2 h-8 rounded border border-gray-200" style={{ backgroundColor: themeSettings.header_bg_color }} />
            </div>

            {/* Content Background */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content Area Background</label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={themeSettings.content_bg_color}
                  onChange={(e) => setThemeSettings(prev => ({ ...prev, content_bg_color: e.target.value }))}
                  className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  disabled={!isSuperAdmin}
                />
                <input
                  type="text"
                  value={themeSettings.content_bg_color}
                  onChange={(e) => setThemeSettings(prev => ({ ...prev, content_bg_color: e.target.value }))}
                  className="flex-1 p-2 border border-gray-300 rounded-lg text-sm uppercase"
                  placeholder="#FFFFFF"
                  disabled={!isSuperAdmin}
                />
              </div>
              <div className="mt-2 h-8 rounded border border-gray-200" style={{ backgroundColor: themeSettings.content_bg_color }} />
            </div>

            {/* AI Trainer / Chat Window Background */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">AI Trainer / Chat Window Background</label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={themeSettings.chat_bg_color}
                  onChange={(e) => setThemeSettings(prev => ({ ...prev, chat_bg_color: e.target.value }))}
                  className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  disabled={!isSuperAdmin}
                />
                <input
                  type="text"
                  value={themeSettings.chat_bg_color}
                  onChange={(e) => setThemeSettings(prev => ({ ...prev, chat_bg_color: e.target.value }))}
                  className="flex-1 p-2 border border-gray-300 rounded-lg text-sm uppercase"
                  placeholder="#FFFFFF"
                  disabled={!isSuperAdmin}
                />
              </div>
              <div className="mt-2 h-8 rounded border border-gray-200" style={{ backgroundColor: themeSettings.chat_bg_color }} />
            </div>
          </div>

          {/* Live Preview */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden h-24">
              <div className="w-16 flex items-center justify-center text-[10px] text-gray-500 font-medium" style={{ backgroundColor: themeSettings.sidebar_bg_color, borderRight: '1px solid #e5e7eb' }}>
                Sidebar
              </div>
              <div className="flex-1 flex flex-col">
                <div className="h-8 flex items-center px-3 text-[10px] text-gray-500 font-medium" style={{ backgroundColor: themeSettings.header_bg_color, borderBottom: '1px solid #e5e7eb' }}>
                  Header
                </div>
                <div className="flex-1 flex items-center justify-center text-[10px] text-gray-500 font-medium" style={{ backgroundColor: themeSettings.content_bg_color }}>
                  Content
                </div>
              </div>
              <div className="w-12 flex items-center justify-center text-[10px] text-gray-500 font-medium" style={{ backgroundColor: themeSettings.chat_bg_color, borderLeft: '1px solid #e5e7eb' }}>
                AI
              </div>
            </div>
          </div>
        </Card>
        )}

        {/* Pricing Models Subchapter */}
        {activeTab === 'pricing' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <DollarSign className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Pricing Models</h2>
            </div>
          </div>
          
          <p className="text-sm text-gray-600 mb-6">
            Manage the features displayed for each subscription plan on the Subscription Dashboard and Pricing page.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { type: 'core', name: 'Core Plan', icon: '🚀', color: 'bg-blue-50 border-blue-200' },
              { type: 'advanced', name: 'Advanced Plan', icon: '⭐', color: 'bg-purple-50 border-purple-200' },
              { type: 'enterprise', name: 'Enterprise Plan', icon: '👑', color: 'bg-amber-50 border-amber-200' }
            ].map((plan) => {
              const planData = pricingPlans[plan.type];
              const featureCount = planData?.features?.length || 0;
              const includedCount = planData?.features?.filter(f => f.is_included).length || 0;

              return (
                <div 
                  key={plan.type}
                  className={`p-4 rounded-lg border-2 ${plan.color} transition-all hover:shadow-md`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{plan.icon}</span>
                      <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Features:</span>
                      <Badge variant="outline">{featureCount}</Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Included:</span>
                      <Badge className="bg-green-100 text-green-800">{includedCount}</Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Not Included:</span>
                      <Badge className="bg-gray-100 text-gray-600">{featureCount - includedCount}</Badge>
                    </div>
                  </div>

                  {isSuperAdmin ? (
                    <Button 
                      onClick={() => openPlanFeaturesModal(plan.type)}
                      className="w-full bg-[#0056B3] hover:bg-blue-700"
                      size="sm"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit Features
                    </Button>
                  ) : (
                    <Button 
                      variant="outline"
                      className="w-full"
                      size="sm"
                      disabled
                    >
                      View Only
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {!isSuperAdmin && (
            <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
              <p className="text-sm text-yellow-800">
                Only Super Admin can edit pricing plan features.
              </p>
            </div>
          )}
        </Card>
        )}

        {/* The Solution Page CMS Subchapter */}
        {activeTab === 'solution' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Layout className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>The Solution Page</h2>
            </div>
            {canManageSolutionPage && (
              <Button
                onClick={handleSaveSolutionContent}
                className="bg-[#0056B3] hover:bg-blue-700"
                disabled={solutionSaving}
              >
                {solutionSaving ? 'Saving...' : 'Save Solution Content'}
              </Button>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Manage all public content for The Solution page. Updates reflect automatically without any code deployment.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Main Page Heading</label>
              <Input
                value={solutionContent.main_heading}
                onChange={(e) => setSolutionContent(prev => ({ ...prev, main_heading: e.target.value }))}
                placeholder="The solution"
                disabled={!canManageSolutionPage}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Enhancing Staff Skills Title</label>
              <Input
                value={solutionContent.staff_skills_title}
                onChange={(e) => setSolutionContent(prev => ({ ...prev, staff_skills_title: e.target.value }))}
                placeholder="Enhancing staff skills..."
                disabled={!canManageSolutionPage}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Main Description Text</label>
              <textarea
                value={solutionContent.main_description}
                onChange={(e) => setSolutionContent(prev => ({ ...prev, main_description: e.target.value }))}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Main description shown under the heading"
                disabled={!canManageSolutionPage}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Enhancing Staff Skills Description</label>
              <textarea
                value={solutionContent.staff_skills_description}
                onChange={(e) => setSolutionContent(prev => ({ ...prev, staff_skills_description: e.target.value }))}
                rows={2}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Short supporting text for staff skills section"
                disabled={!canManageSolutionPage}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">HTMS Section Title</label>
              <Input
                value={solutionContent.htms_title}
                onChange={(e) => setSolutionContent(prev => ({ ...prev, htms_title: e.target.value }))}
                placeholder="Healthcare training management system"
                disabled={!canManageSolutionPage}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">HTMS Section Content</label>
              <Input
                value={solutionContent.htms_content}
                onChange={(e) => setSolutionContent(prev => ({ ...prev, htms_content: e.target.value }))}
                placeholder="Enhanced staff skills for optimal care"
                disabled={!canManageSolutionPage}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">HTMS Right-side Media URL (Optional Fallback)</label>
              <Input
                value={solutionContent.htms_right_image_url}
                onChange={(e) => setSolutionContent(prev => ({ ...prev, htms_right_image_url: e.target.value, htms_right_media_url: e.target.value }))}
                placeholder="https://..."
                disabled={!canManageSolutionPage}
              />
              <p className="text-xs text-gray-500 mt-2">
                This URL is used when no uploaded S3 media is configured or available.
              </p>
            </div>
            <div className="md:col-span-2 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">HTMS Right-side Media Upload</p>
                  <p className="text-xs text-gray-600">Upload image or video for secure storage and signed URL preview.</p>
                </div>
                {canManageSolutionPage && (
                  <label className="cursor-pointer inline-flex items-center px-3 py-2 bg-[#0056B3] text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                    <Upload className="w-4 h-4 mr-2" />
                    {solutionMediaUploading ? 'Uploading...' : 'Upload Media'}
                    <input
                      type="file"
                      accept="image/*,video/*"
                      className="hidden"
                      onChange={handleUploadSolutionMedia}
                      disabled={solutionMediaUploading}
                    />
                  </label>
                )}
              </div>

              {solutionContent.htms_right_media_url && (
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <p className="text-xs text-gray-500 mb-2">Current Preview ({solutionContent.htms_right_media_type === 'video' ? 'Video' : 'Image'})</p>
                  {solutionContent.htms_right_media_type === 'video' ? (
                    <video
                      src={solutionContent.htms_right_media_url}
                      controls
                      className="w-full max-h-64 rounded object-cover"
                    />
                  ) : (
                    <img
                      src={solutionContent.htms_right_media_url}
                      alt="HTMS media preview"
                      className="w-full max-h-64 rounded object-cover"
                    />
                  )}
                </div>
              )}

              {solutionContent.htms_right_media_s3_key && (
                <p className="text-xs text-gray-500 mt-2 break-all">
                  S3 key: {solutionContent.htms_right_media_s3_key}
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Supporting Description Below HTMS</label>
              <textarea
                value={solutionContent.htms_supporting_description}
                onChange={(e) => setSolutionContent(prev => ({ ...prev, htms_supporting_description: e.target.value }))}
                rows={2}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Supporting description shown below HTMS section"
                disabled={!canManageSolutionPage}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Example of Course Included Title</label>
              <Input
                value={solutionContent.courses_section_title}
                onChange={(e) => setSolutionContent(prev => ({ ...prev, courses_section_title: e.target.value }))}
                placeholder="Examples of courses included"
                disabled={!canManageSolutionPage}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Example of Course Included Content</label>
              <Input
                value={solutionContent.courses_section_description}
                onChange={(e) => setSolutionContent(prev => ({ ...prev, courses_section_description: e.target.value }))}
                placeholder="Section subtitle"
                disabled={!canManageSolutionPage}
              />
            </div>
          </div>

          <div className="border-t pt-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">HTMS Pointer List</h3>
              {canManageSolutionPage && (
                <Button variant="outline" size="sm" onClick={handleAddPointer}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Pointer
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Add, edit, remove, and reorder the pointers shown in the left-side HTMS list.
            </p>

            <div className="space-y-2">
              {(solutionContent.feature_points || []).map((point, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={point}
                    onChange={(e) => handleUpdatePointer(index, e.target.value)}
                    placeholder={`Pointer ${index + 1}`}
                    disabled={!canManageSolutionPage}
                  />
                  {canManageSolutionPage && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReorderPointer(index, 'up')}
                        disabled={index === 0}
                      >
                        Up
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReorderPointer(index, 'down')}
                        disabled={index === (solutionContent.feature_points || []).length - 1}
                      >
                        Down
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleDeletePointer(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="font-semibold text-gray-900 mb-3">Cards Management</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add, edit, delete, and reorder solution cards. Each card supports title, description, icon key, and optional image URL.
            </p>

            {canManageSolutionPage && (
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Add New Card</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    value={newSolutionCard.title}
                    onChange={(e) => setNewSolutionCard(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Card title"
                  />
                  <Input
                    value={newSolutionCard.icon_key}
                    onChange={(e) => setNewSolutionCard(prev => ({ ...prev, icon_key: e.target.value }))}
                    placeholder="Icon key (flame, bar_chart, shield...)"
                  />
                  <div className="md:col-span-2">
                    <Input
                      value={newSolutionCard.image_url}
                      onChange={(e) => setNewSolutionCard(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="Optional image URL"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <textarea
                      value={newSolutionCard.description}
                      onChange={(e) => setNewSolutionCard(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Card description"
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <Button
                    onClick={handleAddSolutionCard}
                    className="bg-[#0056B3] hover:bg-blue-700"
                    disabled={solutionSaving}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {solutionSaving ? 'Adding...' : 'Add Card'}
                  </Button>
                </div>
              </div>
            )}

            {solutionLoading && (
              <div className="text-sm text-gray-600">Loading Solution cards...</div>
            )}

            {!solutionLoading && solutionCards.length === 0 && (
              <div className="text-sm text-gray-600">No Solution cards available.</div>
            )}

            <div className="space-y-3">
              {!solutionLoading && solutionCards.map((card, index) => (
                <div key={card.card_id || index} className="border border-gray-200 rounded-lg p-4 bg-white">
                  {editingSolutionCardId === card.card_id ? (
                    <div className="space-y-3">
                      <Input
                        value={editingSolutionCard.title}
                        onChange={(e) => setEditingSolutionCard(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Card title"
                      />
                      <Input
                        value={editingSolutionCard.icon_key}
                        onChange={(e) => setEditingSolutionCard(prev => ({ ...prev, icon_key: e.target.value }))}
                        placeholder="Icon key"
                      />
                      <Input
                        value={editingSolutionCard.image_url}
                        onChange={(e) => setEditingSolutionCard(prev => ({ ...prev, image_url: e.target.value }))}
                        placeholder="Optional image URL"
                      />
                      <textarea
                        value={editingSolutionCard.description}
                        onChange={(e) => setEditingSolutionCard(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Card description"
                        rows={3}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={cancelEditSolutionCard}>Cancel</Button>
                        <Button
                          onClick={() => handleUpdateSolutionCard(card.card_id)}
                          className="bg-[#0056B3] hover:bg-blue-700"
                          disabled={solutionSaving}
                        >
                          {solutionSaving ? 'Saving...' : 'Save Card'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">{card.title}</p>
                        <p className="text-xs text-gray-500 mt-1">Icon: {card.icon_key || 'none'}{card.image_url ? ' | Image URL configured' : ''}</p>
                        <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{card.description}</p>
                      </div>

                      {canManageSolutionPage && (
                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReorderSolutionCard(card.card_id, 'up')}
                            disabled={index === 0 || solutionSaving}
                          >
                            Move Up
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReorderSolutionCard(card.card_id, 'down')}
                            disabled={index === solutionCards.length - 1 || solutionSaving}
                          >
                            Move Down
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEditSolutionCard(card)}
                            disabled={solutionSaving}
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleDeleteSolutionCard(card.card_id)}
                            disabled={solutionSaving}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!canManageSolutionPage && (
              <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <p className="text-sm text-yellow-800">
                  Only Super Admin and Exlva Admin can manage The Solution page content.
                </p>
              </div>
            )}
          </div>
        </Card>
        )}

        {/* FAQ Management Subchapter */}
        {activeTab === 'faqs' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Layout className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>FAQ Management</h2>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Manage FAQs shown on the landing page. Changes are reflected automatically without code updates.
          </p>

          {canManageFaqs && (
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Add New FAQ</h3>
              <div className="space-y-3">
                <Input
                  value={newFaqQuestion}
                  onChange={(e) => setNewFaqQuestion(e.target.value)}
                  placeholder="Enter FAQ question"
                />
                <textarea
                  value={newFaqAnswer}
                  onChange={(e) => setNewFaqAnswer(e.target.value)}
                  placeholder="Enter FAQ answer"
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleAddFaq}
                    className="bg-[#0056B3] hover:bg-blue-700"
                    disabled={faqSaving}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {faqSaving ? 'Adding...' : 'Add FAQ'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {faqLoading && (
              <div className="text-sm text-gray-600">Loading FAQs...</div>
            )}

            {!faqLoading && faqs.length === 0 && (
              <div className="text-sm text-gray-600">No FAQs available.</div>
            )}

            {!faqLoading && faqs.map((faq, index) => (
              <div key={faq.faq_id || index} className="border border-gray-200 rounded-lg p-4 bg-white">
                {editingFaqId === faq.faq_id ? (
                  <div className="space-y-3">
                    <Input
                      value={editingFaqQuestion}
                      onChange={(e) => setEditingFaqQuestion(e.target.value)}
                      placeholder="Enter FAQ question"
                    />
                    <textarea
                      value={editingFaqAnswer}
                      onChange={(e) => setEditingFaqAnswer(e.target.value)}
                      placeholder="Enter FAQ answer"
                      rows={4}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={cancelEditFaq}>
                        Cancel
                      </Button>
                      <Button
                        onClick={() => handleUpdateFaq(faq.faq_id)}
                        className="bg-[#0056B3] hover:bg-blue-700"
                        disabled={faqSaving}
                      >
                        {faqSaving ? 'Saving...' : 'Save FAQ'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">{faq.question}</p>
                        <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{faq.answer}</p>
                      </div>

                      {canManageFaqs && (
                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReorderFaq(faq.faq_id, 'up')}
                            disabled={index === 0 || faqSaving}
                          >
                            Move Up
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReorderFaq(faq.faq_id, 'down')}
                            disabled={index === faqs.length - 1 || faqSaving}
                          >
                            Move Down
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEditFaq(faq)}
                            disabled={faqSaving}
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleDeleteFaq(faq.faq_id)}
                            disabled={faqSaving}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {!canManageFaqs && (
            <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
              <p className="text-sm text-yellow-800">
                Only Super Admin and Exlva Admin can manage FAQs.
              </p>
            </div>
          )}
        </Card>
        )}

        {/* Privacy Policy Subchapter */}
        {activeTab === 'landing' && (
        <>
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Layout className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Privacy Policy</h2>
            </div>
            {isSuperAdmin && (
              <Button 
                onClick={() => handleSavePolicy('privacy')}
                className="bg-[#0056B3] hover:bg-blue-700"
              >
                Save Privacy Policy
              </Button>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Privacy Policy Content
            </label>
            <ReactQuill
              theme="snow"
              value={privacyPolicy}
              onChange={setPrivacyPolicy}
              readOnly={!isSuperAdmin}
              placeholder="Enter privacy policy content here..."
              className={`bg-white ${!isSuperAdmin ? 'cursor-not-allowed opacity-60' : ''}`}
              modules={{
                toolbar: isSuperAdmin ? [
                  [{ 'header': [1, 2, 3, false] }],
                  [{ 'font': [] }, { 'size': [] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'color': [] }, { 'background': [] }],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'align': [] }],
                  ['link'],
                  ['clean']
                ] : false
              }}
              style={{ minHeight: '300px' }}
            />
            <p className="text-sm text-gray-600 mt-2">
              This content will be displayed on the Privacy Policy page accessible at <code className="bg-gray-100 px-2 py-1 rounded">/privacy-policy</code>
            </p>
          </div>
        </Card>

        {/* Terms & Conditions Subchapter */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Layout className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Terms & Conditions</h2>
            </div>
            {isSuperAdmin && (
              <Button 
                onClick={() => handleSavePolicy('terms')}
                className="bg-[#0056B3] hover:bg-blue-700"
              >
                Save Terms & Conditions
              </Button>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Terms & Conditions Content
            </label>
            <ReactQuill
              theme="snow"
              value={termsConditions}
              onChange={setTermsConditions}
              readOnly={!isSuperAdmin}
              placeholder="Enter terms and conditions content here..."
              className={`bg-white ${!isSuperAdmin ? 'cursor-not-allowed opacity-60' : ''}`}
              modules={{
                toolbar: isSuperAdmin ? [
                  [{ 'header': [1, 2, 3, false] }],
                  [{ 'font': [] }, { 'size': [] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'color': [] }, { 'background': [] }],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'align': [] }],
                  ['link'],
                  ['clean']
                ] : false
              }}
              style={{ minHeight: '300px' }}
            />
            <p className="text-sm text-gray-600 mt-2">
              This content will be displayed on the Terms & Conditions page accessible at <code className="bg-gray-100 px-2 py-1 rounded">/terms-and-conditions</code>
            </p>
          </div>
        </Card>
        </>
        )}

        {activeTab === 'socialMedia' && (
          <Card className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Share2 className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Social Media Channels</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Manage social media links displayed in the landing page footer. Only active channels are shown to visitors.
            </p>

            {/* Add New Channel */}
            {canManageBranding && (
              <div className="border rounded-lg p-4 mb-6 bg-gray-50">
                <h3 className="text-sm font-semibold mb-3">Add New Channel</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    placeholder="Channel name (e.g. LinkedIn)"
                    value={newSocialName}
                    onChange={e => setNewSocialName(e.target.value)}
                  />
                  <Input
                    placeholder="URL (e.g. https://linkedin.com/company/...)"
                    value={newSocialUrl}
                    onChange={e => setNewSocialUrl(e.target.value)}
                  />
                </div>
                <button
                  onClick={handleAddSocialChannel}
                  disabled={socialSaving || !newSocialName.trim() || !newSocialUrl.trim()}
                  className="mt-3 inline-flex items-center px-4 py-2 bg-[#0056B3] text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {socialSaving ? 'Adding...' : 'Add Channel'}
                </button>
              </div>
            )}

            {/* Channel List */}
            {socialLoading ? (
              <p className="text-sm text-gray-500">Loading channels...</p>
            ) : socialChannels.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Share2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No social media channels configured yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {socialChannels.map(channel => (
                  <div key={channel.channel_id} className={`border rounded-lg p-4 ${channel.is_active ? 'bg-white' : 'bg-gray-50 opacity-75'}`}>
                    {editingSocialId === channel.channel_id ? (
                      <div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          <Input
                            value={editingSocialData.name}
                            onChange={e => setEditingSocialData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Channel name"
                          />
                          <Input
                            value={editingSocialData.url}
                            onChange={e => setEditingSocialData(prev => ({ ...prev, url: e.target.value }))}
                            placeholder="URL"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdateSocialChannel(channel.channel_id)}
                            disabled={socialSaving}
                            className="inline-flex items-center px-3 py-1.5 bg-[#0056B3] text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                          >
                            <Check className="w-3.5 h-3.5 mr-1" />
                            Save
                          </button>
                          <button
                            onClick={() => { setEditingSocialId(null); setEditingSocialData({ name: '', url: '' }); }}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-100"
                          >
                            <X className="w-3.5 h-3.5 mr-1" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {/* Icon Preview */}
                          <div className="w-10 h-10 rounded-full bg-gray-100 border flex items-center justify-center overflow-hidden flex-shrink-0">
                            {channel.icon_url ? (
                              <img src={channel.icon_url} alt={channel.name} className="w-6 h-6 object-contain" />
                            ) : (
                              <Share2 className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{channel.name}</span>
                              <Badge variant={channel.is_active ? 'default' : 'secondary'} className={`text-xs ${channel.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                {channel.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            <a href={channel.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5">
                              {channel.url.length > 50 ? channel.url.substring(0, 50) + '...' : channel.url}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>

                        {canManageBranding && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Upload Icon */}
                            <label className="cursor-pointer p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Upload icon">
                              <Upload className="w-4 h-4 text-gray-500" />
                              <input
                                type="file"
                                accept=".png,.svg,.jpg,.jpeg,.webp,.ico"
                                className="hidden"
                                onChange={e => handleSocialIconUpload(channel.channel_id, e)}
                                disabled={socialIconUploading === channel.channel_id}
                              />
                            </label>
                            {/* Edit */}
                            <button
                              onClick={() => { setEditingSocialId(channel.channel_id); setEditingSocialData({ name: channel.name, url: channel.url }); }}
                              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4 text-gray-500" />
                            </button>
                            {/* Toggle Active */}
                            <button
                              onClick={() => handleToggleSocialChannel(channel.channel_id, channel.is_active)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                              title={channel.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {channel.is_active ? (
                                <ToggleRight className="w-5 h-5 text-green-600" />
                              ) : (
                                <ToggleLeft className="w-5 h-5 text-gray-400" />
                              )}
                            </button>
                            {/* Delete */}
                            <button
                              onClick={() => handleDeleteSocialChannel(channel.channel_id, channel.name)}
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-4">
              Upload custom icons for each channel (PNG, SVG recommended). Icons are displayed in the footer in the order shown above.
            </p>
          </Card>
        )}

        {activeTab === 'integrations' && (
          <Card className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Layout className="w-6 h-6 text-[#0056B3]" />
              <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Integrations</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Manage third-party services and provider setup from the Integration Settings page.
            </p>
            <button
              onClick={() => navigate('/admin/integration-settings')}
              className="inline-flex items-center px-4 py-2 bg-[#0056B3] text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Open Integration Settings
            </button>
          </Card>
        )}

        </div>
      </div>

      {/* Plan Features Edit Modal */}
      <Dialog open={planFeaturesModalOpen} onOpenChange={setPlanFeaturesModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-[22px] font-bold" style={{ color: '#0056B3' }}>
              Edit {editingPlan === 'core' ? 'Core' : editingPlan === 'advanced' ? 'Advanced' : 'Enterprise'} Plan Features
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 mt-4">
          <div className="space-y-4">
            {/* Add New Feature */}
            <div className="flex space-x-2">
              <Input
                placeholder="Enter new feature..."
                value={newFeatureText}
                onChange={(e) => setNewFeatureText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                className="flex-1"
              />
              <Button onClick={addFeature} className="bg-[#0056B3] hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>

            {/* Features List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {editingFeatures.map((feature, index) => (
                <div 
                  key={index}
                  className={`flex items-center space-x-3 p-3 rounded-lg border transition-all ${
                    feature.is_included ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                  
                  <Input
                    value={feature.text}
                    onChange={(e) => updateFeatureText(index, e.target.value)}
                    className={`flex-1 ${feature.is_bold ? 'font-semibold' : ''}`}
                  />
                  
                  <div className="flex items-center space-x-2">
                    {/* Toggle Included */}
                    <button
                      onClick={() => toggleFeatureIncluded(index)}
                      className={`p-1.5 rounded transition-colors ${
                        feature.is_included 
                          ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                      data-tooltip={feature.is_included ? 'Included in plan' : 'Not included in plan'}
                    >
                      {feature.is_included ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </button>
                    
                    {/* Toggle Bold */}
                    <button
                      onClick={() => toggleFeatureBold(index)}
                      className={`p-1.5 rounded font-bold transition-colors ${
                        feature.is_bold 
                          ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                      data-tooltip={feature.is_bold ? 'Bold text' : 'Normal text'}
                    >
                      B
                    </button>
                    
                    {/* Delete */}
                    <button
                      onClick={() => removeFeature(index)}
                      className="p-1.5 rounded bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                      data-tooltip="Remove feature"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              
              {editingFeatures.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No features added yet. Add your first feature above.</p>
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="flex items-center space-x-4 text-xs text-gray-500 pt-2 border-t">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded bg-green-100 border border-green-200"></div>
                <span>Included</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200"></div>
                <span>Not Included</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="font-bold text-blue-600">B</span>
                <span>Bold/Featured</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setPlanFeaturesModalOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={savePlanFeatures}
                className="bg-[#0056B3] hover:bg-blue-700"
                disabled={pricingLoading}
              >
                {pricingLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CMSSettingsPage;
